// Replies: every prospect who has written back, who is waiting on us, and the
// thread itself. Read from the Podcasts table and the Thread Messages table
// that thread_sync.py fills from Gmail.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { api } from "../services";
import type { Defined, RepliesBoard, RepliesThread, ReplyRow } from "../services/types";
import { PageHead, SectionHead } from "../components/dash/PageHead";
import { StatTiles } from "../components/dash/StatTiles";
import { DefinitionsPanel } from "../components/dash/DefinitionsPanel";
import { DataTable, type Column } from "../components/dash/DataTable";
import { formatISTDateTime } from "../lib/readAgo";

const TILES = ["replies.waiting_on_you", "replies.halted", "replies.total"];

function when(x: string | undefined): number {
  const t = x ? new Date(x).getTime() : NaN;
  return Number.isNaN(t) ? 0 : t;
}

function Thread({ record }: { record: string }) {
  const q = useQuery({
    queryKey: ["replies:thread", record],
    queryFn: async () => {
      const r = await api.replies.thread(record);
      if (!r.ok) throw new Error(r.error);
      return r as unknown as RepliesThread;
    },
  });
  if (q.isPending) return <p className="empty">Reading the thread.</p>;
  if (q.isError) return <div className="note-neg"><p>{(q.error as Error).message}</p></div>;
  const msgs = [...(q.data?.messages ?? [])].sort((a, b) => when(b.at || b.date) - when(a.at || a.date));
  if (msgs.length === 0) return <p className="empty">No messages are synced for this thread yet.</p>;
  return (
    <div className="thread">
      {msgs.map((m, i) => {
        const us = m.direction === "Outbound";
        return (
          <div className="msgrow" key={`${m.at}-${i}`}>
            <div className={`who${us ? " us" : ""}`}>
              <span>{us ? `Us, ${m.mailbox || m.from}` : m.from}</span>
              <span>{formatISTDateTime(m.at || m.date)}</span>
              {m.step ? <span>{m.step}</span> : null}
              {m.auto_reply ? <span>auto reply</span> : null}
            </div>
            {m.subject ? <div className="subj">{m.subject}</div> : null}
            <div className="body">{m.body}</div>
          </div>
        );
      })}
    </div>
  );
}

export function Replies() {
  const [sp, setSp] = useSearchParams();
  const openRecord = sp.get("record");
  const [waitingOnly, setWaitingOnly] = useState(false);

  const boardQ = useQuery({
    queryKey: ["replies:board", waitingOnly],
    queryFn: async () => {
      const r = await api.replies.board(waitingOnly, 0);
      if (!r.ok) throw new Error(r.error);
      return r as Defined<RepliesBoard>;
    },
    staleTime: 120_000,
  });

  function toggle(record: string) {
    const n = new URLSearchParams(sp);
    if (openRecord === record) n.delete("record");
    else n.set("record", record);
    setSp(n, { replace: true });
  }

  const columns = useMemo<Column<ReplyRow>[]>(
    () => [
      {
        key: "show", label: "Show",
        cell: (r) => <>{r.channel || "(unnamed)"}{r.halted ? <span className="dim">halted</span> : null}</>,
      },
      { key: "person", label: "Person", cell: (r) => r.host || r.email || "" },
      { key: "last", label: "Last inbound IST", cell: (r) => formatISTDateTime(r.latest_reply_date) },
      { key: "mailbox", label: "Mailbox", cell: (r) => (r.sender || "").split("@")[0] || "none" },
      {
        key: "back", label: "Replied back",
        cell: (r) => (r.we_replied_back ? r.we_replied_back.toLowerCase() : r.waiting_on_you ? "no" : "yes"),
      },
      {
        key: "thread", label: "Show thread",
        cell: (r) => (
          <button type="button" className="site" onClick={() => toggle(r.record)}>
            {openRecord === r.record ? "Hide thread" : "Show thread"}
          </button>
        ),
      },
    ],
    [openRecord, sp],
  );

  const d = boardQ.data;
  const s = d?.summary;

  return (
    <>
      <PageHead
        title="Replies"
        caption="Every prospect who has written back, read from Airtable and the synced Gmail threads."
      />

      <section className="section compact">
        <SectionHead title="Today" />
        {boardQ.isError ? (
          <div className="note-neg"><p>{(boardQ.error as Error).message}</p></div>
        ) : !d ? (
          <p className="empty">Reading Airtable.</p>
        ) : (
          <StatTiles
            tiles={[
              { id: "replies.waiting_on_you", value: s?.waiting_on_you },
              { id: "replies.halted", value: s?.halted },
              { id: "replies.total", value: s?.total },
            ]}
            definitions={d.definitions}
            readAt={d.read_at}
          />
        )}
      </section>

      <section className="section compact">
        <SectionHead
          title="Threads"
          right={
            <div className="kws">
              <button type="button" className={`kw${waitingOnly ? " on" : ""}`} onClick={() => setWaitingOnly(true)}>
                Waiting on you {s?.waiting_on_you ?? 0}
              </button>
              <button type="button" className={`kw${!waitingOnly ? " on" : ""}`} onClick={() => setWaitingOnly(false)}>
                All {s?.total ?? 0}
              </button>
            </div>
          }
        />
        {boardQ.isError ? null : (
          <DataTable
            columns={columns}
            rows={d?.items ?? []}
            rowKey={(r) => r.record}
            loading={boardQ.isPending}
            loadingText="Reading Airtable."
            empty={waitingOnly ? "No prospect is waiting on a reply." : "No prospect has replied yet."}
            after={(r) => (openRecord === r.record ? <Thread record={r.record} /> : null)}
          />
        )}
      </section>

      {d ? <DefinitionsPanel ids={TILES} definitions={d.definitions} readAt={d.read_at} /> : null}
    </>
  );
}
